// src/pages/Allrecipe.jsx
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { Card, CardBody } from "./../../components/card/card.jsx";
import glassshimmer from "../../assets/images/glassshimmer.png";
import Select from "react-select";
import { fetchAllsearchRecipes } from "../../actions/recipes_actions";
import CraftedCocktailsExpandCard from "../../components/charts/CraftedCocktailsExpandCard";

const typeOptions = [
  { value: "all",     label: "All"     },
  { value: "curated", label: "Curated" },
  { value: "custom",  label: "Custom"  },
];

const Allrecipe = () => {
  const dispatch   = useDispatch();
  const themeColor = getComputedStyle(document.body).getPropertyValue("--bs-theme").trim();

  const {
    search_allrecipes_info: allRecipes,
    loading_search_allrecipes: isLoading,
    search_allrecipes_error: error,
  } = useSelector(state => state.recipes);

  // ── Applied (committed) filters for API ───────────────────────────────────
  const [appliedType,        setAppliedType]        = useState(typeOptions[0]);
  const [appliedLimit,       setAppliedLimit]       = useState(10);
  const [appliedSearchParam, setAppliedSearchParam] = useState(""); // "" or term (>=3 chars)

  // ── Local (editable) filters ──────────────────────────────────────────────
  const [localType,       setLocalType]       = useState(appliedType);
  const [localLimit,      setLocalLimit]      = useState(appliedLimit);
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  // Pagination (client-side over the fetched list)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = appliedLimit;
  const totalItems   = allRecipes.length;

  // Freeze UI & show spinner overlay while applying/loading
  const [isApplying, setIsApplying] = useState(false);
  const pendingApplyRef = useRef(null);

  // Initial fetch on mount
  useEffect(() => {
    dispatch(fetchAllsearchRecipes(appliedType.value, String(appliedLimit), appliedSearchParam));
  }, [dispatch]); // run once

  // After spinner paints, commit & fetch (only when there are changes)
  useEffect(() => {
    if (!isApplying || !pendingApplyRef.current) return;

    const { nextType, nextLimit, nextSearch } = pendingApplyRef.current;

    // commit "applied" filters so repeated clicks with same values do nothing
    setAppliedType(nextType);
    setAppliedLimit(nextLimit);
    setAppliedSearchParam(nextSearch);
    setCurrentPage(1);

    dispatch(fetchAllsearchRecipes(nextType.value, String(nextLimit), nextSearch));
  }, [isApplying, dispatch]);

  // Turn off applying once Redux loading finishes
  const prevLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      setIsApplying(false);
      pendingApplyRef.current = null;
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  // Apply button — do nothing if unchanged (compares effective params)
  const handleApply = () => {
    const trimmed = (localSearchTerm || "").trim();
    const nextSearch = trimmed === "" || trimmed.length >= 3 ? trimmed : "";

    const changed =
      localType.value !== appliedType.value ||
      Number(localLimit) !== Number(appliedLimit) ||
      nextSearch !== appliedSearchParam;

    if (!changed) return; // <- skip request

    const nextLimit = Math.max(1, Number(localLimit) || 1);
    pendingApplyRef.current = { nextType: localType, nextLimit, nextSearch };
    setIsApplying(true);
  };

  // Client-side pagination over fetched results
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd   = currentPage * itemsPerPage;
  const pageSlice = allRecipes.slice(pageStart, pageEnd);

  const hideList = isApplying || isLoading;

  return (
    <>
      {/* Header */}
      <div style={{ paddingBottom: 10 }}>
        <h6>
          <span style={{ color: themeColor }}>PAGES</span> / ALL RECIPES
        </h6>
        <h1 className="page-header mb-0">ALL RECIPES</h1>
      </div>

      {/* Crafted Cocktails (reusable card with its own apply/freeze logic) */}
      <Card>
        <CardBody>
          <CraftedCocktailsExpandCard />
        </CardBody>
      </Card>

      {/* Results */}
      <div className="col-xl-12">
        <Card className="mt-3">
          <CardBody>
            <div className="row mb-3">
              <h4>ALL RECIPES</h4>

              {/* Filters (apply-on-click) */}
              <div className="col-12">
                <div className="d-flex justify-content-center align-items-end flex-wrap gap-3">
                  <div style={{ minWidth: 260 }}>
                    <label>Type</label>
                    <Select
                      options={typeOptions}
                      value={localType}
                      onChange={setLocalType}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: base => ({ ...base, zIndex: 11000 }) }}
                    />
                  </div>

                  <div style={{ minWidth: 160 }}>
                    <label>Limit</label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={localLimit}
                      onChange={e => setLocalLimit(e.target.value)}
                      placeholder="Limit"
                    />
                  </div>

                  <div style={{ minWidth: 320 }}>
                    <label>Search</label>
                    <Form.Control
                      type="text"
                      placeholder="min 3 chars"
                      value={localSearchTerm}
                      onChange={e => setLocalSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Apply */}
                  <div className="d-flex align-items-end">
                    <button className="btn btn-secondary" onClick={handleApply} type="button">
                      <i className="fas fa-search" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="d-flex justify-content-end mb-2 fs-6">
              {isLoading ? (
                <small>Loading...</small>
              ) : (
                <small>
                  {totalItems === 0 ? (
                    "Showing 0 of 0"
                  ) : (
                    <>
                      <span className="text-inverse">Showing </span>
                      <span style={{ color: themeColor }}>
                        {Math.max(pageStart + 1, 1)}
                      </span>
                      <span className="text-inverse"> of </span>
                      <span style={{ color: themeColor }}>
                        {Math.min(pageEnd, totalItems)}
                      </span>
                    </>
                  )}
                </small>
              )}
            </div>

            {/* List with spinner overlay; keep content mounted to avoid flicker */}
            <div className="position-relative" style={{ minHeight: 220 }}>
              <div style={{ visibility: hideList ? "hidden" : "visible" }}>
                {error ? (
                  <div className="text-danger">{error}</div>
                ) : totalItems === 0 ? (
                  <div>No Records Found</div>
                ) : (
                  pageSlice.map((product, index) => (
                    <div key={product.id || index} className="mb-3 p-3 product_border">
                      <div className="row">
                        <div className="col-xl-8 col-12 col-md-6 col">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-2 d-flex align-items-center justify-content-center me-2 text-theme-900"
                              style={{ fontWeight: "700", minHeight: "20px", minWidth: "20px" }}
                            >
                              {pageStart + index + 1}
                            </div>
                            <div className="position-relative">
                              <img
                                src={product.image || glassshimmer}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = glassshimmer;
                                }}
                                alt={product.name}
                                className="img-fluid"
                                style={{
                                  width: "5rem",
                                  height: "5rem",
                                  objectFit: "cover",
                                  borderRadius: "0.25rem",
                                  backgroundColor: "#fff",
                                }}
                              />
                            </div>
                            <div className="flex-1 ps-3">
                              <div className="fw-500 text-inverse">{product.name}</div>
                              <div>
                                <span className="pe-2 fw-600">Crafted By: </span>
                                {product.username}
                              </div>
                              <div>
                                <span className="pe-2 fw-600">Created On: </span>
                                <span>{product.created}</span>
                              </div>
                            </div>
                            <div className="flex-1 ms-2">
                              <table>
                                <tbody>
                                  <tr>
                                    <td className="pe-2 fw-600" style={{ minWidth: 60 }}>Views:</td>
                                    <td className="pe-2 fw-500" style={{ minWidth: 40 }}>{product.viewCount}</td>

                                    <td className="pe-2 fw-600" style={{ minWidth: 60 }}>Likes:</td>
                                    <td className="pe-2 fw-500" style={{ minWidth: 40 }}>{product.likeCount}</td>

                                    <td className="pe-2 fw-600" style={{ minWidth: 60 }}>Crafted:</td>
                                    <td className="pe-2 fw-500" style={{ minWidth: 40 }}>{product.makeCount}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <div className="col-xl-4 col-12">
                          <div>
                            <label className="fw-600">Base:</label>
                            <span className="ms-4">{product.baseList}</span>
                          </div>
                          <div>
                            <label className="fw-600">Mixer:</label>
                            <span className="ms-4">{product.mixerList}</span>
                          </div>
                          <div>
                            <label className="fw-600">Garnish:</label>
                            <span className="ms-2">{product.garnishList}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {hideList && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
                  <div className="spinner-border" style={{ color: themeColor }} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* If decided to re-enable server-side pagination later. */}
        {/* <div className="row mt-3">
          <div className="col-md-9" />
          <div className="col-md-3">
            <Paginationpagewise
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              paginate={(p) => setCurrentPage(p)}
            />
          </div>
        </div> */}
      </div>
    </>
  );
};

export default Allrecipe;